/**
 * Constants for video state management and asset type identification
 */

/**
 * Asset type string constants
 * These correspond to the simplified asset type representations used throughout the video state system
 */
export const ASSET_TYPES = {
    TEXT: 'TEXT',
    READONLY_TEXT: 'READONLY_TEXT',
    FANCY_CAPTION: 'FANCY_CAPTION',
    CAPTION: 'CAPTION',
    MEDIA: 'MEDIA',
    READONLY_MEDIA: 'READONLY_MEDIA',
    ICON: 'ICON',
    READONLY_ICON: 'READONLY_ICON',
    STICKER: 'STICKER',
    BACKGROUND: 'BACKGROUND',
    GROUP: 'GROUP',
    WATERMARK: 'WATERMARK',
    CHART: 'CHART',
    MOTION_GRAPHIC: 'MOTION_GRAPHIC',
} as const;

/**
 * Type for asset type string values
 */
export type AssetType = (typeof ASSET_TYPES)[keyof typeof ASSET_TYPES];

/**
 * Scene group type string constants
 * These correspond to the different types of scene groups in the video
 */
export const SCENE_GROUP_TYPES = {
    REGULAR: 'regular',
    AI_VOICEOVER: 'ai_voiceover',
    RECORDED_VOICEOVER: 'recorded_voiceover',
    AI_TALKING_HEAD: 'ai_talking_head',
    RECORDED_TALKING_HEAD: 'recorded_talking_head',
} as const;

/**
 * Type for scene group type string values
 */
export type SceneGroupType = (typeof SCENE_GROUP_TYPES)[keyof typeof SCENE_GROUP_TYPES];

/**
 * Reserved colour filter names the agent reads in the video state and passes to the colour grading
 * tools alongside the real filter names: NONE is a video or media asset showing no filter, GLOBAL
 * tells set_media_effects to drop an asset's override so it follows the video's filter again, and
 * GREYSCALE is the name of the greyscale filter (Luminary models it as a switch, not a graded filter).
 */
export const COLOR_FILTER_NONE = 'none';
export const COLOR_FILTER_GLOBAL = 'global';
export const GREYSCALE_COLOR_FILTER_NAME = 'Mono';

/**
 * Message returned in place of a video state when no video has been created yet
 * (i.e. the video has no scenes). Surfacing this instead of an empty diff keeps
 * agents from treating a not-yet-created video as if it existed.
 */
export const NO_VIDEO_STATE_MESSAGE =
    'There is no video yet. No video has been created, so there is no video state to show.';

/**
 * Sentinel for a serialized handle whose underlying value is genuinely absent or not applicable —
 * e.g. an asset that isn't a media/icon asset, or an icon color map with no target color. Kept
 * distinct from UNREGISTERED (present-but-unresolved) so the agent can tell "there is nothing here"
 * apart from "there is something here I haven't looked up". Deliberately not the old vague
 * `<unknown>`, which conflated the two.
 */
export const ABSENT = '<absent>';

/**
 * Sentinel for a serialized handle whose asset is present and carries a uuid, but that uuid isn't in
 * the agent's registry yet (e.g. a template default the agent hasn't searched up). Kept distinct
 * from ABSENT (genuinely nothing) and from a resolved id. Deliberately not the old vague `<unknown>`.
 */
export const UNREGISTERED = '<unregistered>';

/**
 * Sentinel for the presenter slot of a talking-head scene: a media asset linked to its scene group
 * (`linkToSceneGroup`), which mirrors the group's recording rather than holding media of its own.
 * Reported instead of a registry lookup because the group's uuid changes when audio enhancement
 * lands, and a slot flipping from a real id to UNREGISTERED reads as "media removed" — which is
 * exactly what tempted the agent to refill it with `set_media` and break the link.
 */
export const TALKING_HEAD_MEDIA = '<talking head>';

/**
 * Sentinel id used in a MusicDescription when the video has genuinely no music set. Reuses ABSENT
 * (there is no music), distinct from a set-but-unresolved track, which reports UNREGISTERED. The
 * diff formatter keys off this to render a single "No music set" line instead of dumping placeholder
 * id/volume/title fields.
 */
export const NO_MUSIC_ID = ABSENT;
